import { useCallback } from 'react';
import { contractService, EContractStatus } from '../../services/contracts/ContractService';
import { ContractResponseDto } from '../../services/contracts/ContractService';
import { normalizeContractStatus } from '../../utils/contractStatusUtils';

const EDITABLE_STATUSES: EContractStatus[] = [
  EContractStatus.Criado,
  EContractStatus.Assinado,
  EContractStatus.AguardandoCorrecaoConsultor,
  EContractStatus.AguardandoAssinatura,
  EContractStatus.NaoAssinado,
  EContractStatus.RecusadoCQV,
  EContractStatus.RecusadoQualidade,
  EContractStatus.RecusadoCVP,
  EContractStatus.TrocaDeCarteira,
  EContractStatus.TrocaDeCarteiraSolicitada,
];

export interface UseContractActionsOptions {
  loadContracts: (page?: number) => Promise<void>;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  currentPage: number;
  /** Abre o modal de criação em modo edição (mesmo modal para criar/editar) */
  openContractForEditInCreateModal: (contract: ContractResponseDto) => void | Promise<void>;
  setContractToEdit: (contract: ContractResponseDto | null) => void;
  setShowEditWarningModal: (show: boolean) => void;
  /** IDs dos contratos cujos dados serão enviados por e-mail (grupo misto = vários). */
  setSendingEmailContractIds: (ids: string[] | null) => void;
  setShowSendEmailModal: (show: boolean) => void;
}

/**
 * Hook com ações sobre contratos: deletar, editar, enviar email.
 * Outros handlers (changeStatus, CQV, VTME, duplicate) permanecem na página
 * por dependerem de mais estado/modal e podem ser migrados depois.
 */
export function useContractActions(options: UseContractActionsOptions) {
  const {
    loadContracts,
    showSuccess,
    showError,
    currentPage,
    openContractForEditInCreateModal,
    setContractToEdit,
    setShowEditWarningModal,
    setSendingEmailContractIds,
    setShowSendEmailModal,
  } = options;

  const handleDeleteContract = useCallback(
    async (contractId: string) => {
      if (!window.confirm('Tem certeza que deseja deletar este contrato? Todos os ICCIDs vinculados serão liberados.')) {
        return;
      }
      try {
        await contractService.deleteContract(contractId);
        await loadContracts(currentPage);
        showSuccess('Contrato deletado com sucesso!');
      } catch (error) {
        const err = error as { response?: { status?: number; data?: { message?: string; success?: boolean } }; message?: string };
        console.error('Erro ao deletar contrato:', error);

        if (err.response?.status === 404) {
          await loadContracts(currentPage);
          showSuccess('Contrato removido. A lista foi atualizada.');
          return;
        }

        const apiMessage = err.response?.data?.message?.trim();
        showError(apiMessage || 'Erro ao deletar contrato');
      }
    },
    [loadContracts, currentPage, showSuccess, showError]
  );

  const handleSendContractEmail = useCallback(
    (contractIds: string[]) => {
      setSendingEmailContractIds(contractIds.length > 0 ? contractIds : null);
      setShowSendEmailModal(true);
    },
    [setSendingEmailContractIds, setShowSendEmailModal]
  );

  const handleEditContract = useCallback(
    async (contractId: string) => {
      try {
        const contract = await contractService.getContractById(contractId);

        // API serializa enum como string camelCase; normalize evita falso negativo (ex.: "criado" !== 0).
        const statusNorm = normalizeContractStatus(
          contract.status as number | string | null | undefined
        );
        if (statusNorm !== null && !EDITABLE_STATUSES.includes(statusNorm)) {
          showError('Não é possível editar um contrato neste status. A edição só é permitida nos status iniciais.');
          return;
        }

        const hoursSinceCreation = (Date.now() - new Date(contract.createdAt).getTime()) / (1000 * 60 * 60);
        if (hoursSinceCreation > 24) {
          setContractToEdit(contract);
          setShowEditWarningModal(true);
        } else {
          await openContractForEditInCreateModal(contract);
        }
      } catch (error) {
        console.error('Erro ao carregar contrato para edição:', error);
        showError('Erro ao carregar contrato para edição');
      }
    },
    [openContractForEditInCreateModal, setContractToEdit, setShowEditWarningModal, showError]
  );

  return {
    handleDeleteContract,
    handleSendContractEmail,
    handleEditContract,
  };
}
